import React from "react";
import {
  Megaphone,
  BarChart3,
  Search,
  PenLine,
  Share2,
  Target,
  Palette,
  CheckCircle2,
} from "lucide-react";
import HeaderMenu from "../../component/Navbar/headermenu";
import CustomFooter from "../../component/Footer/customFooter";
import ShowcaseImageLightbox from "../../component/ShowcaseImageLightbox";
import "./about.css";
import taskTable from "../../assets/marketing/m-tasks.png";
import peekView from "../../assets/marketing/m-peekview.png";
import dashboard from "../../assets/marketing/m-dashboard.png";
import clients from "../../assets/marketing/m-client.png";
import billFlow from "../../assets/marketing/billflow.png";

const features = [
  {
    icon: <BarChart3 size={26} />,
    title: "Analytics & Reporting",
    description:
      "Monthly marketing dashboards, performance reports, and client scorecards tracked as tasks with client, period, and due date — from data pull to final delivery.",
  },
  {
    icon: <Search size={26} />,
    title: "SEO",
    description:
      "Metadata optimization, keyword audits, and search performance reviews organized by client and campaign — so SEO work doesn't live in a separate spreadsheet.",
  },
  {
    icon: <PenLine size={26} />,
    title: "Content Marketing",
    description:
      "Content briefs, draft reviews, and client approvals tracked with clear owners and deadlines — from first draft to published piece.",
  },
  {
    icon: <Share2 size={26} />,
    title: "Social Media Management",
    description:
      "Caption drafts, post scheduling, and platform reviews tracked per client — so nothing goes live without the right sign-off.",
  },
];

const showcase = [
  {
    eyebrow: "Every Campaign, One Task Board",
    title: "SEO, content, social, paid ads, and analytics — tracked in one place",
    description:
      "Analytics & Reporting, SEO, Content Marketing, Social Media Management, Paid Advertising, Brand Strategy, and Campaign Management — every deliverable for Global Retail, TechNova Solutions, FinEdge Group, Acme Corporation, and Sunrise Industries tracked with status, period, financial year, and assignee in one board.",
    image: taskTable,
    alt: "Simplifyte task table showing marketing campaigns across Analytics and Reporting, SEO, Content Marketing, Social Media Management, and Paid Advertising with client and status columns",
  },
  {
    eyebrow: "Every Deliverable, With Full Context",
    title: "Open any campaign task without losing the bigger picture",
    description:
      "A task like 'Prepare monthly marketing dashboard' opens in a side peek panel with its client (Global Retail), financial year, period, due date, and assignee — full context, without leaving the campaign list behind.",
    image: peekView,
    alt: "Simplifyte task peek panel showing Prepare monthly marketing dashboard for Global Retail with financial year, due date, and assignee",
  },
  {
    eyebrow: "Account Dashboard",
    title: "Every client account's workload, the moment you log in",
    description:
      "Today's tasks, a performance percentage, and a live breakdown across To Do, In Progress, Under Review, and Closed — plus upcoming events and reminders — so account managers start the day already knowing what's on track and what needs a nudge.",
    image: dashboard,
    alt: "Simplifyte dashboard showing today's tasks, performance percentage, task status breakdown, and upcoming events and reminders for a marketing team",
  },
  {
    eyebrow: "Client Accounts",
    title: "Every client, organized with industry and contact details",
    description:
      "Horizon Estates, Greenfield Developers, TechNova Solutions, Acme Corporation — every client account in one list with industry, contact, client groups, and login access — so account managers aren't digging through spreadsheets to find who owns what.",
    image: clients,
    alt: "Simplifyte Clients screen showing active client accounts including Horizon Estates, Greenfield Developers, TechNova Solutions, and Acme Corporation with industry and contact columns",
  },
  {
    eyebrow: "Bill Flow",
    title: "Closed campaign work, ready to invoice",
    description:
      "Every closed task flows into Bill Flow with billed, unbilled, and non-billable counts per client — so finance teams know exactly what's been invoiced and what's still waiting, without reconciling timesheets at month-end.",
    image: billFlow,
    alt: "Simplifyte Bill Flow screen showing billed and unbilled closed tasks grouped by client with status and task counts",
  },
];

const benefits = [
  "Track every campaign, content piece, and client deliverable in one board",
  "See client, period, and assignee context without status meetings",
  "Give account managers a daily view of what's due and overdue",
  "Manage all client accounts with industry and contact details in one place",
  "Bill closed campaign work without reconciling timesheets at month-end",
];

const MarketingAgenciesPage = () => {
  return (
    <>
      <HeaderMenu />
      <div className="Industry_class_main">
        {/* Hero */}
        <section className="Industry_class_hero">
          <div className="Industry_class_container">
            <div className="Industry_class_hero_content">
              <div className="Industry_class_icon_box">
                <Megaphone size={34} />
              </div>

              <h1 className="Industry_class_heading">
                Campaign and deliverable management software for Marketing
                agencies
              </h1>

              <p className="Industry_class_subheading">
                Track campaigns, content production, client accounts, and
                billable deliverables in one place your whole team can trust.
              </p>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="Industry_class_features_section">
          <div className="Industry_class_container">
            <h2 className="Industry_class_section_title Industry_class_center">
              Everything a marketing agency's back office needs
            </h2>

            <div className="Industry_class_features_grid">
              {features.map((feature, index) => (
                <div key={index} className="Industry_class_feature_card">
                  <div className="Industry_class_feature_icon">
                    {feature.icon}
                  </div>

                  <h3 className="Industry_class_feature_title">
                    {feature.title}
                  </h3>

                  <p className="Industry_class_feature_description">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Product Showcase */}
        <section className="Industry_class_showcase_section">
          <div className="Industry_class_container">
            <h2 className="Industry_class_section_title Industry_class_center">
              Built for the pace of client campaigns
            </h2>
            <p className="Industry_class_showcase_subtitle">
              Real screens from the product — campaign boards, account
              dashboards, client management, and billing your team can rely on.
            </p>

            <div className="Industry_class_showcase_list">
              {showcase.map((item, index) => (
                <div
                  key={index}
                  className={`Industry_class_showcase_row ${
                    index % 2 === 1 ? "Industry_class_showcase_row_reverse" : ""
                  }`}
                >
                  <div className="Industry_class_showcase_text">
                    <span className="Industry_class_showcase_eyebrow">
                      {item.eyebrow}
                    </span>
                    <h3 className="Industry_class_showcase_title">
                      {item.title}
                    </h3>
                    <p className="Industry_class_showcase_description">
                      {item.description}
                    </p>
                  </div>

                  <div className="Industry_class_showcase_media">
                    <div className="Industry_class_showcase_frame_outer">
                      <div className="Industry_class_showcase_frame_inner">
                        <ShowcaseImageLightbox
                          src={item.image}
                          alt={item.alt}
                          className="Industry_class_showcase_image"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="Industry_class_benefits_section">
          <div className="Industry_class_container">
            <div className="Industry_class_benefits_wrapper">
              <h2 className="Industry_class_section_title Industry_class_center">
                Why marketing agencies choose Simplifyte
              </h2>

              <div className="Industry_class_benefits_list">
                {benefits.map((benefit, index) => (
                  <div key={index} className="Industry_class_benefit_item">
                    <CheckCircle2
                      size={24}
                      className="Industry_class_check_icon"
                    />

                    <p>{benefit}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="Industry_class_cta_section">
          <div className="Industry_class_container">
            <div className="Industry_class_cta_box">
              <h2 className="Industry_class_cta_title">
                Ready to simplify your campaigns?
              </h2>

              <p className="Industry_class_cta_text">
                Join marketing and creative agencies who've brought clarity to
                campaigns, client accounts, and billing.
              </p>

              <button
                className="Industry_class_cta_button"
                onClick={() =>
                  (window.location.href = "https://app.simplifyte.com")
                }
              >
                Start Free Trial
              </button>
            </div>
          </div>
        </section>
      </div>
      <CustomFooter />
    </>
  );
};

export default MarketingAgenciesPage;
